import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { onAuthStateChanged, signOut, User } from 'firebase/auth';
import { auth, db } from './firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';

const SESSION_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const LAST_ACTIVE_KEY = 'crm:lastActiveAt';
const HEARTBEAT_MS = 15 * 60 * 1000; // update timestamp every 15 min

function touchLastActive() {
  try { window.localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now())); } catch { /* ignore */ }
}

function clearLastActive() {
  try { window.localStorage.removeItem(LAST_ACTIVE_KEY); } catch { /* ignore */ }
}

interface AuthContextType {
  currentUser: User | null;
  actorUser: User | null;
  userRole: string | null;
  roleId: string | null;
  projectIds: string[];
  officeProjectIds: string[];
  currentProjectId: string | null;
  setCurrentProjectId: (projectId: string | null) => void;
  permissions: Permissions;
  loading: boolean;
  canUseViewAs: boolean;
  viewingAs: ViewAsUser | null;
  startViewAs: (user: ViewAsUserInput) => Promise<void>;
  stopViewAs: () => void;
}

export type ViewAsUserInput = {
  uid: string;
  email: string;
  name: string;
  roleId: string;
  projectIds: string[];
  officeProjectIds?: string[];
  pageAccess?: string[];
  actionAccess?: string[];
  officePageAccess?: string[];
  officeActionAccess?: string[];
};

export type ViewAsUser = ViewAsUserInput & { permissions: Permissions };

export type Permissions = {
  pages: Record<string, boolean>;
  actions: Record<string, Record<string, boolean>>;
};

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  actorUser: null,
  userRole: null,
  roleId: null,
  projectIds: [],
  officeProjectIds: [],
  currentProjectId: null,
  setCurrentProjectId: () => {},
  permissions: { pages: {}, actions: {} },
  loading: true,
  canUseViewAs: false,
  viewingAs: null,
  startViewAs: async () => {},
  stopViewAs: () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

export function usePermissions() {
  const { permissions, roleId } = useAuth();
  const isAdmin = String(roleId || '') === 'super-admin' || String(roleId || '') === 'admin';
  const canPage = (pageKey: string) => {
    if (pageKey === 'seo') return isAdmin || !!permissions.pages?.seo;
    return !!permissions.pages?.[pageKey];
  };
  const canAction = (domain: string, action: string) =>
    !!permissions.actions?.[domain]?.[action];
  return { permissions, canPage, canAction };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [roleId, setRoleId] = useState<string | null>(null);
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [officeProjectIds, setOfficeProjectIds] = useState<string[]>([]);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<Permissions>({ pages: {}, actions: {} });
  const [viewingAs, setViewingAs] = useState<ViewAsUser | null>(null);
  const [loading, setLoading] = useState(true);

  const applyUserBasedPageRules = (
    nextPermissions: Permissions,
    resolvedRoleId: string,
    userPageAccess: string[] | null
  ): Permissions => {
    const pages = { ...(nextPermissions.pages || {}) };
    const explicitPages = new Set((userPageAccess || []).map((value) => String(value || '').trim()));
    const isAdmin = resolvedRoleId === 'super-admin' || resolvedRoleId === 'admin';

    pages.jobApplications =
      resolvedRoleId === 'super-admin' || explicitPages.has('jobApplications');

    pages.seo = isAdmin || explicitPages.has('seo');

    return {
      pages,
      actions: nextPermissions.actions || {},
    };
  };

  const defaultPermissions = useMemo(
    () => ({
      superAdmin: {
        pages: { dashboard: true, leads: true, followUps: true, brokers: true, jobApplications: true, messages: true, campaigns: true, reports: true, settings: true, seo: true },
        actions: {
          leads: { addLead: true, addBroker: true, changeAssign: true, updateFollowUp: true, editLead: true },
          settings: { addUser: true, addProject: true },
          brokers: { addBroker: true, editBroker: true, deleteBroker: true },
        },
      },
      admin: {
        pages: { dashboard: true, leads: true, followUps: true, brokers: true, jobApplications: true, messages: true, campaigns: true, reports: true, settings: false, seo: true },
        actions: {
          leads: { addLead: true, addBroker: true, changeAssign: true, updateFollowUp: true, editLead: true },
          settings: { addUser: false, addProject: false },
          brokers: { addBroker: true, editBroker: true, deleteBroker: true },
        },
      },
      sales: {
        pages: { dashboard: true, leads: true, followUps: true, brokers: true, jobApplications: false, messages: false, reports: false, settings: false },
        actions: {
          leads: { addLead: true, addBroker: false, changeAssign: false, updateFollowUp: true, editLead: false },
          settings: { addUser: false, addProject: false },
          brokers: { addBroker: false, editBroker: false, deleteBroker: false },
        },
      },
      'sales-manager': {
        pages: { dashboard: true, leads: true, followUps: true, brokers: true, jobApplications: false, messages: false, reports: false, settings: false },
        actions: {
          leads: { addLead: true, addBroker: false, changeAssign: true, updateFollowUp: true, editLead: false },
          settings: { addUser: false, addProject: false },
          brokers: { addBroker: true, editBroker: true, deleteBroker: true },
        },
      },
      'sales-team': {
        pages: { dashboard: true, leads: true, followUps: true, brokers: true, jobApplications: false, messages: false, reports: false, settings: false },
        actions: {
          leads: { addLead: true, addBroker: false, changeAssign: false, updateFollowUp: true, editLead: false },
          settings: { addUser: false, addProject: false },
          brokers: { addBroker: true, editBroker: false, deleteBroker: false },
        },
      },
      trainee: {
        pages: { dashboard: true, leads: true, followUps: false, brokers: false, jobApplications: false, messages: false, reports: false, settings: false },
        actions: {
          leads: { addLead: false, addBroker: false, changeAssign: false, updateFollowUp: false, editLead: false },
          settings: { addUser: false, addProject: false },
          brokers: { addBroker: false, editBroker: false, deleteBroker: false },
        },
      },
    }),
    []
  );

  const permissionsFromKeys = (
    resolvedRoleId: string,
    pageKeys: string[],
    actionKeys: string[]
  ): Permissions => {
    const pages: Record<string, boolean> = {};
    for (const key of pageKeys) pages[key] = true;
    pages.settings = resolvedRoleId === 'super-admin';

    const actions: Record<string, Record<string, boolean>> = {};
    for (const raw of actionKeys) {
      if (!raw.includes('.')) continue;
      const [domain, action] = raw.split('.');
      if (!domain || !action) continue;
      if (!actions[domain]) actions[domain] = {};
      actions[domain][action] = true;
    }
    return applyUserBasedPageRules({ pages, actions }, resolvedRoleId, pageKeys);
  };

  const startViewAs = async (target: ViewAsUserInput) => {
    if (String(currentUser?.email || '').trim().toLowerCase() !== 'shetajatin18@gmail.com') return;
    const resolvedRoleId = String(target.roleId || 'user').trim().toLowerCase();
    const pageKeys = [...(target.pageAccess || []), ...(target.officePageAccess || [])];
    const actionKeys = [...(target.actionAccess || []), ...(target.officeActionAccess || [])];
    let targetPermissions: Permissions;

    if (resolvedRoleId !== 'super-admin' && (pageKeys.length || actionKeys.length)) {
      targetPermissions = permissionsFromKeys(resolvedRoleId, pageKeys, actionKeys);
    } else {
      try {
        const roleDoc = await getDoc(doc(db, 'roles', resolvedRoleId));
        if (roleDoc.exists()) {
          const data = roleDoc.data() as Permissions;
          targetPermissions = applyUserBasedPageRules(
            { pages: data.pages || {}, actions: data.actions || {} },
            resolvedRoleId,
            target.pageAccess || null
          );
        } else {
          const preset = resolvedRoleId === 'super-admin'
            ? defaultPermissions.superAdmin
            : resolvedRoleId === 'admin'
              ? defaultPermissions.admin
              : defaultPermissions.sales;
          targetPermissions = applyUserBasedPageRules(preset, resolvedRoleId, target.pageAccess || null);
        }
      } catch {
        const preset = resolvedRoleId === 'super-admin'
          ? defaultPermissions.superAdmin
          : resolvedRoleId === 'admin'
            ? defaultPermissions.admin
            : defaultPermissions.sales;
        targetPermissions = applyUserBasedPageRules(preset, resolvedRoleId, target.pageAccess || null);
      }
    }

    // Preview is deliberately read-only even if the selected user can normally edit.
    setViewingAs({
      ...target,
      roleId: resolvedRoleId,
      permissions: { pages: targetPermissions.pages || {}, actions: {} },
    });
  };

  const stopViewAs = () => setViewingAs(null);

  useEffect(() => {
    let unsubscribeUserDocument: (() => void) | undefined;
    let isCurrentAuthSession = true;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      unsubscribeUserDocument?.();
      unsubscribeUserDocument = undefined;
      setCurrentUser(user);
      if (user) {
        // ── 7-day session expiry ────────────────────────────────────
        const lastActive = Number(window.localStorage.getItem(LAST_ACTIVE_KEY) || '0');
        if (lastActive && Date.now() - lastActive > SESSION_EXPIRY_MS) {
          clearLastActive();
          await signOut(auth);
          return; // onAuthStateChanged fires again with user=null
        }
        touchLastActive();
        // ────────────────────────────────────────────────────────────

        // Restore the last selected project for this user (if any).
        if (typeof window !== 'undefined') {
          const saved = window.localStorage.getItem(`crm:selectedProject:${user.uid}`);
          setCurrentProjectId(saved || null);
        } else {
          setCurrentProjectId(null);
        }

        const tokenResult = await user.getIdTokenResult();
        const roleClaim = tokenResult.claims.role;
        const fallbackRole = typeof roleClaim === 'string' ? roleClaim : 'user';
        setUserRole(fallbackRole);

        const applyUserDocument = async (userData: Record<string, unknown> | undefined) => {
          if (!isCurrentAuthSession) return;

          // Re-evaluate access whenever an administrator changes this user record.
          let resolvedRoleId = fallbackRole;
          let userPageAccess: string[] | null = null;
          let userActionAccess: string[] | null = null;
          let officePageAccess: string[] | null = null;
          let officeActionAccess: string[] | null = null;

          try {
          if (userData?.roleId && typeof userData.roleId === 'string') {
            resolvedRoleId = userData.roleId;
          }
          const projIds = userData?.projectIds;
          if (Array.isArray(projIds)) {
            setProjectIds(projIds.filter((x: any) => typeof x === 'string'));
          } else {
            setProjectIds([]);
          }

          const pages = userData?.pageAccess;
          if (Array.isArray(pages)) {
            userPageAccess = pages.filter((x: any) => typeof x === 'string' && x.trim());
          }
          const actions = userData?.actionAccess;
          if (Array.isArray(actions)) {
            userActionAccess = actions.filter((x: any) => typeof x === 'string' && x.trim());
          }
          const officeProjects = userData?.officeProjectIds;
          setOfficeProjectIds(Array.isArray(officeProjects) ? officeProjects.filter((x: any) => typeof x === 'string') : []);
          const officePages = userData?.officePageAccess;
          if (Array.isArray(officePages)) {
            officePageAccess = officePages.filter((x: any) => typeof x === 'string' && x.trim());
          }
          const officeActions = userData?.officeActionAccess;
          if (Array.isArray(officeActions)) {
            officeActionAccess = officeActions.filter((x: any) => typeof x === 'string' && x.trim());
          }
          } catch (err) {
            console.warn('Could not read user access', err);
            setProjectIds([]);
            setOfficeProjectIds([]);
          }
          if (!isCurrentAuthSession) return;
          setRoleId(resolvedRoleId);

          // Rule: Settings page stays super-admin only (route-guarded too), regardless of per-user selections.
          const buildFromUserAccess = (pageKeys: string[], actionKeys: string[]): Permissions => {
          const pages: Record<string, boolean> = {};
          for (const k of pageKeys) pages[k] = true;
          pages.settings = resolvedRoleId === 'super-admin';

          const actions: Record<string, Record<string, boolean>> = {};
          for (const raw of actionKeys) {
            if (typeof raw !== 'string' || !raw.includes('.')) continue;
            const [domain, action] = raw.split('.');
            if (!domain || !action) continue;
            if (!actions[domain]) actions[domain] = {};
            actions[domain][action] = true;
          }
          return applyUserBasedPageRules(
            { pages, actions },
            resolvedRoleId,
            pageKeys
          );
          };

          // If user has explicit pageAccess/actionAccess and is not super-admin,
          // prefer these over role defaults so per-user access works.
          const allPageAccess = [...(userPageAccess || []), ...(officePageAccess || [])];
          const allActionAccess = [...(userActionAccess || []), ...(officeActionAccess || [])];
          if (resolvedRoleId !== 'super-admin' && (allPageAccess.length || allActionAccess.length)) {
            setPermissions(buildFromUserAccess(allPageAccess, allActionAccess));
            setLoading(false);
            return;
          }

          // Otherwise, fetch permissions from roles collection.
          try {
          const roleDoc = await getDoc(doc(db, 'roles', resolvedRoleId));
          if (roleDoc.exists()) {
            const data = roleDoc.data() as Permissions;
            setPermissions(
              applyUserBasedPageRules(
                {
                  pages: data.pages || {},
                  actions: data.actions || {},
                },
                resolvedRoleId,
                userPageAccess
              )
            );
          } else {
            // fallback defaults
            const preset =
              resolvedRoleId === 'super-admin'
                ? defaultPermissions.superAdmin
                : resolvedRoleId === 'admin'
                  ? defaultPermissions.admin
                  : defaultPermissions.sales;
            setPermissions(
              applyUserBasedPageRules(
                preset,
                resolvedRoleId,
                userPageAccess
              )
            );
          }
          } catch (err) {
          console.warn('Could not fetch role permissions', err);
          const preset =
            resolvedRoleId === 'super-admin'
              ? defaultPermissions.superAdmin
              : resolvedRoleId === 'admin'
                ? defaultPermissions.admin
                : defaultPermissions.sales;
          setPermissions(
            applyUserBasedPageRules(
              preset,
              resolvedRoleId,
              userPageAccess
            )
          );
          }
          setLoading(false);
        };

        // The first snapshot also initializes access; later snapshots apply admin changes live.
        unsubscribeUserDocument = onSnapshot(
          doc(db, 'users', user.uid),
          (snapshot) => {
            void applyUserDocument(snapshot.data() as Record<string, unknown> | undefined);
          },
          (err) => {
            console.warn('Could not listen for user access changes', err);
            void applyUserDocument(undefined);
          }
        );
        return;
      } else {
        setViewingAs(null);
        clearLastActive();
        setUserRole(null);
        setRoleId(null);
        setProjectIds([]);
        setOfficeProjectIds([]);
        setCurrentProjectId(null);
        setPermissions({ pages: {}, actions: {} });
      }
      setLoading(false);
    });

    return () => {
      isCurrentAuthSession = false;
      unsubscribeUserDocument?.();
      unsubscribe();
    };
  }, [defaultPermissions]);

  // Heartbeat: keep lastActiveAt fresh while the user is logged in.
  // Also refreshes on tab focus so idle-overnight sessions still get caught promptly.
  useEffect(() => {
    if (!currentUser) return;
    const tick = () => touchLastActive();
    const intervalId = window.setInterval(tick, HEARTBEAT_MS);
    const onVisible = () => { if (!document.hidden) touchLastActive(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [currentUser]);

  useEffect(() => {
    // Persist selection locally (per user).
    if (typeof window === 'undefined') return;
    if (!currentUser) return;
    const key = `crm:selectedProject:${currentUser.uid}`;
    if (!currentProjectId) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, currentProjectId);
  }, [currentProjectId, currentUser]);

  const effectiveUser = useMemo(() => {
    if (!currentUser || !viewingAs) return currentUser;
    return new Proxy(currentUser, {
      get(target, property, receiver) {
        if (property === 'uid') return viewingAs.uid;
        if (property === 'email') return viewingAs.email;
        if (property === 'displayName') return viewingAs.name;
        const value = Reflect.get(target, property, receiver);
        return typeof value === 'function' ? value.bind(target) : value;
      },
    }) as User;
  }, [currentUser, viewingAs]);

  const canUseViewAs = String(currentUser?.email || '').trim().toLowerCase() === 'shetajatin18@gmail.com';
  const value = {
    currentUser: effectiveUser,
    actorUser: currentUser,
    userRole: viewingAs?.roleId || userRole,
    roleId: viewingAs?.roleId || roleId,
    projectIds: viewingAs?.projectIds || projectIds,
    officeProjectIds: viewingAs?.officeProjectIds || officeProjectIds,
    currentProjectId,
    setCurrentProjectId,
    permissions: viewingAs?.permissions || permissions,
    loading,
    canUseViewAs,
    viewingAs,
    startViewAs,
    stopViewAs,
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
}
